import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useProjectComments, useCreateComment } from "@/hooks/use-project-comments";
import { useUploadDocument, useProjectDocuments, getDocumentUrl, formatFileSize } from "@/hooks/use-project-documents";
import type { ProjectDocument } from "@/hooks/use-project-documents";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { UserLookup } from "@/hooks/use-users";
import iconStarBlue from "@/assets/icons/revebla/rosa_star_blue.svg";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { useMentionComposer } from "@/hooks/use-mention-composer";
import { MentionListbox } from "./MentionListbox";
import { CommentMessage } from "./CommentMessage";

interface ProjectUpdatesPanelProps {
  projectId: string;
  users: UserLookup[];
  className?: string;
}

/**
 * Prosjektoppdateringer (`project_comments`): meldingsliste + skrivefelt med
 * @-tagging og vedlegg. Rammeløs, så samme panel kan stå i
 * `ProjectUpdatesTab` (kort på prosjektsiden) og `ProjectUpdatesDialog`
 * (popup fra /prosjekter og /mine-oppgaver, VIB-2179).
 *
 * IKKE person-til-person-chatten (chat_rooms/chat_messages) — den har egne
 * komponenter under `src/components/chat/`.
 */
export function ProjectUpdatesPanel({ projectId, users, className }: ProjectUpdatesPanelProps) {
  const { data: comments, isLoading } = useProjectComments(projectId);
  const { data: documents } = useProjectDocuments(projectId);
  const createMut = useCreateComment();
  const uploadMut = useUploadDocument();
  const { activeUser: authUser } = useImpersonation();
  const [isUploading, setIsUploading] = useState(false);
  const composer = useMentionComposer({ users });
  const { body, setBody } = composer;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [comments]);

  const handleSend = () => {
    if (!body.trim() || !authUser) return;
    createMut.mutate(
      {
        project_id: projectId,
        author_id: authUser.id,
        body: body.trim(),
        mentioned_user_ids: composer.resolveMentionedIds(),
      },
      { onSuccess: () => composer.reset() },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Åpen @-liste eier tastetrykket (VIB-2541): Enter velger, sender ikke.
    if (composer.handleKeyDown(e)) {
      inputRef.current?.focus();
      return;
    }
    // Send med Cmd/Ctrl+Enter. Plain Enter lager ny linje (default textarea-
    // oppførsel) så lengre statusrapporter kan skrives uten Shift+Enter.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-grow: la textarea-en vokse med innholdet opp til max-height (CSS),
  // deretter intern scroll. Kjøres på hver body-endring, også ved tømming
  // etter send, så ruta krymper tilbake (VIB-1695).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  // Build lookup: comment_id → document for inline attachments
  const docsByCommentId = new Map<string, ProjectDocument>();
  (documents ?? []).forEach((d) => {
    if (d.comment_id) docsByCommentId.set(d.comment_id, d);
  });

  const isImageMime = (mime: string | null) =>
    !!mime && mime.startsWith("image/");

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !authUser) return;

    setIsUploading(true);
    try {
      const commentId = await createMut.mutateAsync({
        project_id: projectId,
        author_id: authUser.id,
        body: `📎 ${file.name}`,
        mentioned_user_ids: [],
      });

      await uploadMut.mutateAsync({
        projectId,
        file,
        uploadedById: authUser.id,
        commentId,
      });
    } catch {
      // Errors handled by mutation onError callbacks
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const commentCount = (comments ?? []).length;

  return (
    <div className={cn("flex-1 flex flex-col min-h-0", className)}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 p-4 pr-2">
        {isLoading ? (
          <div className="flex justify-center py-8"><SrIcon name="loader2" className="w-4 h-4 animate-spin" /></div>
        ) : commentCount === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <img src={iconStarBlue} alt="" className="w-8 h-8 mx-auto mb-2 opacity-15" />
            <p className="text-sm">Ingen oppdateringer ennå. Skriv den første!</p>
          </div>
        ) : (
          (comments ?? []).map((c) => {
            // Sølvrev-uploaded dokument knyttet til denne kommentaren via
            // project_documents.comment_id (egen flyt fra paperclip-knappen).
            // Monday-importerte attachments havner i c.attachments og
            // håndteres internt i CommentMessage.
            const localDoc = docsByCommentId.get(c.id);
            const localDocUrl = localDoc ? getDocumentUrl(localDoc.file_path) : null;
            return (
              <div key={c.id} className="space-y-1">
                <CommentMessage comment={c} projectId={projectId} authUserId={authUser?.id} users={users} />
                {localDoc && localDocUrl && (
                  <div className="ml-11 text-xs">
                    {isImageMime(localDoc.mime_type) ? (
                      <a href={localDocUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                        <img
                          src={localDocUrl}
                          alt={localDoc.file_name}
                          className="max-w-[250px] max-h-[200px] rounded object-cover"
                        />
                      </a>
                    ) : (
                      <a
                        href={localDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/70"
                      >
                        <SrIcon name="file-text" className="w-4 h-4 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{localDoc.file_name}</div>
                          <div className="opacity-75">{formatFileSize(localDoc.file_size)}</div>
                        </div>
                        <SrIcon name="download" className="w-3 h-3 flex-shrink-0 opacity-50" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="relative border-t p-4">
        <MentionListbox composer={composer} />
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            aria-autocomplete="list"
            aria-controls={composer.mentionsOpen ? composer.listboxId : undefined}
            aria-activedescendant={composer.highlightedUser ? composer.optionId(composer.highlightedUser.id) : undefined}
            className="flex-1 min-h-[60px] max-h-[300px] overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
            placeholder={authUser ? "Skriv en statusrapport... Bruk @ for å tagge. ⌘/Ctrl+Enter for å sende" : "Logg inn for å skrive"}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!authUser}
            rows={2}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-10 px-3"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !authUser}
            title="Last opp dokument"
          >
            {isUploading ? (
              <SrIcon name="loader2" className="w-4 h-4 animate-spin" />
            ) : (
              <SrIcon name="paperclip" className="w-4 h-4" />
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept="*/*"
          />
          <Button
            size="sm"
            className="h-10 px-3"
            onClick={handleSend}
            disabled={!body.trim() || createMut.isPending || !authUser}
          >
            {createMut.isPending ? <SrIcon name="loader2" className="w-4 h-4 animate-spin" /> : <SrIcon name="send" className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ProjectUpdatesPanel;
